import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import SimultanisEditor from "./SimultanisEditor";
import '../table/EditorTable.css';

function PartEditor({ partname, availablePlantilles, handleSelectChange }) {
    return (
        <div className="editor-create-part-row">
            <label className="editor-create-part-label">{partname}</label>
            <select
                className="editor-create-form-select"
                onChange={(e) => handleSelectChange(partname, e.target.value)}
            >
                <option value="">-</option>
                {availablePlantilles.map((plantilla, index) => (
                    <option key={index} value={plantilla}>
                        {plantilla}
                    </option>
                ))}
            </select>
        </div>
    );
}

function CreateBundle({ socket, onClose }) {
    const [step, setStep] = useState(1);
    const [availablePlantilles, setAvailablePlantilles] = useState([]);
    const [availableBundles, setAvailableBundles] = useState([]);

    const [bundleName, setBundleName] = useState("");
    const [bundleSubtitol, setBundleSubtitol] = useState("");
    const [bundleNomCurt, setBundleNomCurt] = useState("");
    const [parts, setParts] = useState({
        'Pinya': '',
        'Folre': '',
        'Manilles': '',
        'Puntals': '',
        'Tronc': '',
        'Organització': '',
    });
    const [simultani, setSimultani] = useState(false);
    const [bundlesSimultanis, setBundlesSimultanis] = useState([]);

    const createBundle = (attrs) => {
        socket.emit('.create_bundle', attrs);
        onClose();
    };

    const createBundlesATerra = (attrs) => {
        socket.emit('.create_bundles_a_terra', attrs);
        onClose();
    };

    useEffect(() => {
        socket.emit('.request_plantilles');
        socket.emit('.request_bundles');

        const onPlantilles = (data) => setAvailablePlantilles(data);
        const onBundles = (data) => setAvailableBundles(
            data
                .filter(b => b?.hidden !== true)
                .filter(b => !b.simultani)
                .sort((a, b) => a.nom.localeCompare(b.nom))
        );
        socket.on('.plantilles', onPlantilles);
        socket.on('.bundles', onBundles);

        return () => {
            socket.off('.plantilles', onPlantilles);
            socket.off('.bundles', onBundles);
        };
    }, [socket]);

    const handleSelectChange = (partName, value) => {
        setParts(prevParts => ({
            ...prevParts,
            [partName]: value,
        }));
    };

    const handleSubmit = () => {
        if (bundleName === "") {
            alert("El nom de la prova no pot estar buit");
            return;
        }

        if (simultani) {
            createBundle({
                nom: bundleName,
                subtitol: bundleSubtitol,
                shortName: bundleNomCurt,
                simultani: true,
                bundles: bundlesSimultanis,
            });
        } else {
            createBundle({
                nom: bundleName,
                subtitol: bundleSubtitol,
                shortName: bundleNomCurt,
                parts: parts,
            });

            if (parts['Pinya'] && ['Folre', 'Manilles', 'Puntals'].some(part => parts[part])) {
                const confirm = window.confirm(
                    'Vols crear també les proves a terra?\n' +
                    (parts['Folre'] ? `    - Folre a terra de ${bundleNomCurt}\n` : '') +
                    (parts['Manilles'] ? `    - Manilles a terra de ${bundleNomCurt}\n` : '') +
                    (parts['Puntals'] ? `    - Puntals a terra de ${bundleNomCurt}\n` : '')
                );

                if (confirm) {
                    createBundlesATerra({
                        nom: bundleName,
                        subtitol: bundleSubtitol,
                        shortName: bundleNomCurt,
                        parts: parts,
                    });
                }
            }
        }
    };

    return createPortal(
        <div className="editor-modal-overlay" onClick={onClose}>
            <div
                className="editor-modal"
                style={{ minWidth: 480, maxWidth: 560 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="editor-modal-header">
                    <h3>Crea prova</h3>
                    <div className="editor-step-indicator">
                        <div className={`editor-step-dot${step === 1 ? ' active' : ''}`} />
                        <div className={`editor-step-dot${step === 2 ? ' active' : ''}`} />
                    </div>
                    <button onClick={onClose}>&times;</button>
                </div>

                <div className="editor-modal-body" style={{ maxHeight: 'calc(80vh - 140px)', overflow: 'auto' }}>
                    {step === 1 ? (
                        <>
                            {/* Toggle simultània */}
                            <div className="editor-create-form-field">
                                <label className="editor-toggle">
                                    <input
                                        type="checkbox"
                                        checked={simultani}
                                        onChange={(e) => setSimultani(e.target.checked)}
                                    />
                                    <span className="editor-toggle-track"></span>
                                    <span className="editor-toggle-label">És simultània?</span>
                                </label>
                            </div>

                            {/* Nom + Nom curt */}
                            <div className="editor-create-form-row">
                                <div className="editor-create-form-field">
                                    <label className="editor-create-form-label">
                                        <span className="required">*</span>
                                        Nom
                                    </label>
                                    <input
                                        type="text"
                                        className="editor-create-form-input"
                                        placeholder="Nom de la prova"
                                        value={bundleName}
                                        onChange={(e) => setBundleName(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="editor-create-form-field">
                                    <label className="editor-create-form-label">
                                        Nom curt
                                    </label>
                                    <input
                                        type="text"
                                        className="editor-create-form-input"
                                        placeholder="Abreviatura"
                                        value={bundleNomCurt}
                                        onChange={(e) => setBundleNomCurt(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Subtítol */}
                            <div className="editor-create-part-row">
                                <label className="editor-create-part-label">
                                    Subtítol <span className="hint">(privat per tècnica)</span>
                                </label>
                                <input
                                    type="text"
                                    className="editor-create-form-input"
                                    placeholder={`e.g. "Versió 2026", "Amb cinc agulles", "Sense vents"...`}
                                    value={bundleSubtitol}
                                    onChange={(e) => setBundleSubtitol(e.target.value)}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Parts o Simultanis */}
                            {simultani ? (
                                <SimultanisEditor
                                    availableBundles={availableBundles}
                                    selectedBundles={bundlesSimultanis}
                                    setSelectedBundles={setBundlesSimultanis}
                                />
                            ) : (
                                <div className="editor-create-parts-column">
                                    {Object.keys(parts).map((partname) => (
                                        <PartEditor
                                            key={partname}
                                            partname={partname}
                                            availablePlantilles={availablePlantilles}
                                            handleSelectChange={handleSelectChange}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="editor-modal-footer">
                    {step === 1 ? (
                        <>
                            <button
                                className="editor-modal-btn editor-modal-btn-cancel"
                                onClick={onClose}
                            >
                                Cancel·la
                            </button>
                            <button
                                className="editor-modal-btn editor-modal-btn-confirm"
                                onClick={() => setStep(2)}
                                disabled={!bundleName}
                            >
                                Següent
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className="editor-modal-btn editor-modal-btn-cancel"
                                onClick={() => setStep(1)}
                            >
                                Enrere
                            </button>
                            <button
                                className="editor-modal-btn editor-modal-btn-confirm"
                                onClick={handleSubmit}
                            >
                                Crea prova
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default CreateBundle;
